// Workspace de Prova Agendada (kind=prova). Lida com 4 fases vindas do
// servidor:
//   - "lobby"    → antes do startsAt. Mostra anúncio + countdown.
//   - "running"  → janela aberta. Aluno responde; autosave + heartbeat.
//   - "closed"   → após endsAt, sem release ainda. Mostra "aguardando
//                  liberação do gabarito".
//   - "released" → resultado + gabarito (se disclosure=full) em modo consulta.
//
// Regras de produto (project_prova_agendada.md):
//   - Cronômetro global do SERVIDOR. Sem pausa. Quem entra tarde perde tempo.
//   - Snapshot do banco congelado no startsAt.
//   - Heartbeat 30s pro cockpit.
//   - Anulada sempre conta como respondida.
//
// URL: /student/scheduled-exams/:examId

import { signal } from "@preact/signals";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Flag,
  Hourglass,
  Loader2,
  Pause,
  Play,
  Send,
  Timer,
  XCircle
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  api,
  ApiError,
  type QuestaoStudy,
  type ScheduledExamAnswer,
  type ScheduledExamMeta,
  type ScheduledExamPhase,
  type StudentExamDetailResponse
} from "./api";
import { WorkspaceHeader, type WorkspaceHeaderAction } from "./WorkspaceHeader";
import { QuestionCard } from "./QuestionCard";
import {
  QuestionListSidebar,
  applyAgrupamento as applySharedAgrupamento,
  type QListAgrupamento,
  type QListStatus
} from "./QuestionListSidebar";
import { lightboxImg, Lightbox } from "./StudyWorkspace";
import { formatHMS } from "./ProvaWorkspace";
import { uiPrefs, toggleQuestionListCollapsed } from "./uiPrefs";
import { isPlayerOpen } from "./MusicPlayer";

// =====================
// State
// =====================

interface ScheduledExamState {
  examId: string;
  phase: "loading" | "error" | "lobby" | "running" | "closed" | "released";
  serverPhase: ScheduledExamPhase | null;
  exam: ScheduledExamMeta | null;
  questoes: QuestaoStudy[];
  anuladasIdx: number[];
  // Respostas LOCAIS (espelham o que vai pro servidor). Chave = q.id.
  answers: Record<string, ScheduledExamAnswer>;
  currentIdx: number;
  errorMsg: string | null;
  submitting: boolean;
  // Tempo de questão atual — entrouEm timestamp pra somar elapsed
  questaoEntrouEm: number;
  tempoPorQuestao: Record<string, number>;
  // Período aberto: dados do attempt do aluno (quando ele iniciou e estado de pausa).
  // null quando ainda não criou attempt.
  attemptStartedAt: string | null;
  pauseMeta: { pausedAt: string | null; totalPausedMs: number } | null;
  attemptScore: number | null;
}

export const scheduledExamView = signal<ScheduledExamState | null>(null);
const examAgrupamento = signal<QListAgrupamento>("fonte");
const _examTick = signal<number>(0);
const submitConfirmOpen = signal<boolean>(false);
// Subview da fase released: "result" mostra a NOTA proeminente + breakdowns;
// "revisao" abre o navegador questão-a-questão com gabarito + IA.
const releasedSubView = signal<"result" | "revisao">("result");

const EXAM_PATH_RE = /^\/student\/scheduled-exams\/([^/]+)\/?$/;

// =====================
// Routing
// =====================

export function enterScheduledExamView(
  examId: string,
  opts: { pushHistory?: boolean } = {}
) {
  scheduledExamView.value = {
    examId,
    phase: "loading",
    serverPhase: null,
    exam: null,
    questoes: [],
    anuladasIdx: [],
    answers: {},
    currentIdx: 0,
    errorMsg: null,
    submitting: false,
    questaoEntrouEm: Date.now(),
    tempoPorQuestao: {},
    attemptStartedAt: null,
    pauseMeta: null,
    attemptScore: null
  };
  if (opts.pushHistory !== false) {
    const url = `/student/scheduled-exams/${encodeURIComponent(examId)}`;
    if (window.location.pathname !== url) {
      window.history.pushState({ scheduledExamId: examId }, "", url);
    }
  }
  void load(examId);
}

export function exitScheduledExamView(opts: { pushHistory?: boolean } = {}) {
  stopTicking();
  stopHeartbeat();
  scheduledExamView.value = null;
  if (opts.pushHistory !== false) {
    if (EXAM_PATH_RE.test(window.location.pathname)) {
      window.history.pushState({}, "", "/student/");
    }
  }
}

let popstateBound = false;
export function bindScheduledExamPopstate() {
  if (popstateBound || typeof window === "undefined") return;
  popstateBound = true;
  window.addEventListener("popstate", () => {
    const m = EXAM_PATH_RE.exec(window.location.pathname);
    if (m) {
      const id = decodeURIComponent(m[1]);
      if (!scheduledExamView.value || scheduledExamView.value.examId !== id) {
        enterScheduledExamView(id, { pushHistory: false });
      }
    } else {
      if (scheduledExamView.value) exitScheduledExamView({ pushHistory: false });
    }
  });
}

export function restoreScheduledExamFromUrl() {
  if (typeof window === "undefined") return;
  const m = EXAM_PATH_RE.exec(window.location.pathname);
  if (m) {
    const id = decodeURIComponent(m[1]);
    enterScheduledExamView(id, { pushHistory: false });
  }
}

async function load(examId: string) {
  try {
    const resp = await api.getScheduledExam(examId);
    if (scheduledExamView.value?.examId !== examId) return;
    applyServerState(resp);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    if (scheduledExamView.value?.examId !== examId) return;
    scheduledExamView.value = {
      ...scheduledExamView.value!,
      phase: "error",
      errorMsg: err instanceof Error ? err.message : String(err)
    };
  }
}

function applyServerState(resp: StudentExamDetailResponse) {
  const cur = scheduledExamView.value;
  if (!cur) return;
  // Normaliza answers do attempt → Record<qid, ScheduledExamAnswer>
  const answers: Record<string, ScheduledExamAnswer> = {};
  if (resp.attempt) {
    for (const [qid, raw] of Object.entries(resp.attempt.answers ?? {})) {
      if (typeof raw === "number") {
        answers[qid] = { opcao: raw };
      } else if (raw && typeof raw === "object") {
        const o = (raw as any).opcao;
        if (typeof o === "number") {
          answers[qid] = {
            opcao: o,
            tempoMs: (raw as any).tempoMs,
            respondidaEm: (raw as any).respondidaEm
          };
        }
      }
    }
  }
  const tempoPorQuestao: Record<string, number> = {};
  for (const [qid, a] of Object.entries(answers)) {
    if (typeof a.tempoMs === "number") tempoPorQuestao[qid] = a.tempoMs;
  }
  const newPhase =
    resp.phase === "lobby"
      ? "lobby"
      : resp.phase === "running"
        ? "running"
        : resp.phase === "released"
          ? "released"
          : "closed";

  const pauseMetaRaw = resp.attempt?.pauseMeta as
    | { pausedAt?: string | null; totalPausedMs?: number }
    | null
    | undefined;
  const pauseMeta = pauseMetaRaw
    ? {
        pausedAt: pauseMetaRaw.pausedAt ?? null,
        totalPausedMs: pauseMetaRaw.totalPausedMs ?? 0
      }
    : null;

  scheduledExamView.value = {
    ...cur,
    phase: newPhase,
    serverPhase: resp.phase,
    exam: resp.exam,
    questoes: resp.questoes,
    anuladasIdx: resp.anuladasIdx,
    answers,
    tempoPorQuestao,
    currentIdx: 0,
    questaoEntrouEm: Date.now(),
    attemptStartedAt: resp.attempt?.startedAt ?? null,
    pauseMeta,
    attemptScore:
      resp.attempt && resp.attempt.score != null
        ? Number(resp.attempt.score)
        : null,
    errorMsg: null
  };

  if (newPhase === "running") {
    startTicking();
    startHeartbeat();
  }
  if (newPhase === "lobby") {
    startTicking();
  }
}

// =====================
// Tick + heartbeat
// =====================

let tickInterval: ReturnType<typeof setInterval> | null = null;
let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

function startTicking() {
  if (tickInterval) return;
  tickInterval = setInterval(() => {
    _examTick.value = _examTick.value + 1;
    // Se estávamos em lobby e passou o startsAt, recarrega pra pegar
    // questões — servidor é a fonte de verdade.
    const cur = scheduledExamView.value;
    if (!cur) return;
    if (cur.phase === "lobby" && cur.exam?.startsAt) {
      if (Date.parse(cur.exam.startsAt) <= Date.now()) {
        void load(cur.examId);
      }
    }
    // Se estávamos rodando e passou o endsAt, auto-encerra do lado cliente
    // — o servidor já vai recusar saves; aqui só atualizamos a UI.
    if (cur.phase === "running" && cur.exam?.endsAt) {
      if (Date.parse(cur.exam.endsAt) <= Date.now()) {
        void load(cur.examId);
      }
    }
  }, 1000);
}

function stopTicking() {
  if (tickInterval) {
    clearInterval(tickInterval);
    tickInterval = null;
  }
}

function startHeartbeat() {
  if (heartbeatInterval) return;
  // Roda imediatamente uma vez (entrou na prova → registra attempt) e
  // depois a cada 30s.
  void heartbeatOnce();
  heartbeatInterval = setInterval(() => void heartbeatOnce(), 30_000);
}

async function heartbeatOnce() {
  const cur = scheduledExamView.value;
  if (!cur || cur.phase !== "running") return;
  try {
    await api.heartbeatScheduledExam(cur.examId);
  } catch {
    // silencioso — heartbeat falhar não bloqueia a prova
  }
}

function stopHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

// =====================
// Autosave (debounced)
// =====================

let saveTimer: ReturnType<typeof setTimeout> | null = null;
async function flushSave() {
  saveTimer = null;
  const cur = scheduledExamView.value;
  if (!cur || cur.phase !== "running") return;
  try {
    await api.saveScheduledExamActive(
      cur.examId,
      cur.answers,
      cur.currentIdx
    );
  } catch (err) {
    // Servidor pode recusar com `after_end` quando fechou a janela —
    // recarrega pra atualizar fase.
    if (err instanceof ApiError && err.code === "after_end") {
      void load(cur.examId);
    }
  }
}
function scheduleSave() {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => void flushSave(), 600);
}

// =====================
// Helpers
// =====================

function getQuestaoTempoMs(state: ScheduledExamState, qid: string): number {
  const acumulado = state.tempoPorQuestao[qid] ?? 0;
  const isAtual = state.questoes[state.currentIdx]?.id === qid;
  if (isAtual && state.phase === "running") {
    return acumulado + Math.max(0, Date.now() - state.questaoEntrouEm);
  }
  return acumulado;
}

function setAnswer(qid: string, opcao: number | null) {
  const cur = scheduledExamView.value;
  if (!cur || cur.phase !== "running") return;
  const tempoMs = getQuestaoTempoMs(cur, qid);
  const newAnswers = { ...cur.answers };
  if (opcao === null) {
    delete newAnswers[qid];
  } else {
    newAnswers[qid] = { opcao, tempoMs, respondidaEm: Date.now() };
  }
  scheduledExamView.value = { ...cur, answers: newAnswers };
  scheduleSave();
}

function gotoIdx(idx: number) {
  const cur = scheduledExamView.value;
  if (!cur) return;
  const total = cur.questoes.length;
  if (total === 0) return;
  const clamped = Math.max(0, Math.min(total - 1, idx));
  if (clamped === cur.currentIdx) return;
  // Flush do tempo da questão anterior antes de trocar.
  const oldQid = cur.questoes[cur.currentIdx]?.id;
  const tempoPorQuestao = { ...cur.tempoPorQuestao };
  if (oldQid && cur.phase === "running") {
    const elapsed = Math.max(0, Date.now() - cur.questaoEntrouEm);
    tempoPorQuestao[oldQid] = (tempoPorQuestao[oldQid] ?? 0) + elapsed;
  }
  scheduledExamView.value = {
    ...cur,
    currentIdx: clamped,
    tempoPorQuestao,
    questaoEntrouEm: Date.now()
  };
  scheduleSave();
}

async function submit() {
  const cur = scheduledExamView.value;
  if (!cur || cur.phase !== "running") return;
  // Flush final do save antes de encerrar.
  if (saveTimer) {
    clearTimeout(saveTimer);
    await flushSave();
  } else {
    await flushSave();
  }
  scheduledExamView.value = { ...scheduledExamView.value!, submitting: true };
  try {
    await api.endScheduledExam(cur.examId);
    submitConfirmOpen.value = false;
    // Recarrega pra pegar score + fase atualizada.
    await load(cur.examId);
  } catch (err) {
    scheduledExamView.value = {
      ...scheduledExamView.value!,
      submitting: false,
      errorMsg: err instanceof Error ? err.message : String(err)
    };
  }
}

// =====================
// Pausar/Retomar (Período aberto, allowPause=true)
// =====================
//
// Aluno em open_window pode congelar o cronômetro individual sem perder
// progresso. Servidor recalcula `pauseMeta.totalPausedMs` no resume e impede
// pause em modo live. UI fica disabled enquanto a chamada não volta.

async function togglePause() {
  const cur = scheduledExamView.value;
  if (!cur || cur.phase !== "running" || !cur.exam) return;
  if (cur.exam.examMode !== "open_window" || !cur.exam.allowPause) return;
  const pausando = !cur.pauseMeta?.pausedAt;
  try {
    const r = pausando
      ? await api.pauseScheduledExam(cur.examId)
      : await api.resumeScheduledExam(cur.examId);
    // Endpoint devolve o pauseMeta atualizado; refletimos no state.
    const novo = r.pauseMeta ?? null;
    scheduledExamView.value = {
      ...scheduledExamView.value!,
      pauseMeta: novo
    };
  } catch (err) {
    // Em erro, recarrega pra ficar consistente com o server.
    void load(cur.examId);
  }
}

// =====================
// UI
// =====================

export function ScheduledExamWorkspaceView() {
  const state = scheduledExamView.value;
  if (!state) return null;
  if (state.phase === "loading") return <ExamLoading />;
  if (state.phase === "error") return <ExamError msg={state.errorMsg} />;
  return (
    <>
      {state.phase === "lobby" && <Lobby state={state} />}
      {state.phase === "running" && <Running state={state} />}
      {state.phase === "closed" && <Closed state={state} />}
      {state.phase === "released" && <Released state={state} />}
      {submitConfirmOpen.value && <SubmitConfirmModal state={state} />}
      {lightboxImg.value && <Lightbox image={lightboxImg.value} />}
    </>
  );
}

function ExamLoading() {
  return (
    <div class="space-y-4">
      <Skeleton className="h-20 w-full rounded-md" />
      <Skeleton className="h-[400px] w-full rounded-md" />
    </div>
  );
}

function ExamError({ msg }: { msg: string | null }) {
  return (
    <div class="grid place-items-center py-16">
      <div class="max-w-md space-y-3 rounded-md border border-destructive/30 bg-destructive/5 px-6 py-8 text-center">
        <AlertCircle className="mx-auto size-8 text-destructive" />
        <h2 class="text-base font-bold">Não consegui carregar a prova</h2>
        {msg && <p class="text-sm text-muted-foreground">{msg}</p>}
        <button
          type="button"
          onClick={() => exitScheduledExamView()}
          class="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Voltar
        </button>
      </div>
    </div>
  );
}

// =====================
// Lobby (antes do início)
// =====================

function Lobby({ state }: { state: ScheduledExamState }) {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  _examTick.value;
  const exam = state.exam!;
  const now = Date.now();
  const startsAt = exam.startsAt ? Date.parse(exam.startsAt) : null;
  const restoMs = startsAt ? Math.max(0, startsAt - now) : null;

  return (
    <div class="space-y-4">
      <BackHeader />
      <section class="space-y-6 rounded-md border border-primary/30 bg-primary/5 px-6 py-8 text-center">
        <div class="mx-auto grid size-14 place-items-center rounded-full bg-primary/15 text-primary ring-1 ring-primary/30">
          <Hourglass className="size-7" />
        </div>
        <div class="space-y-2">
          <h2 class="text-2xl font-bold">{exam.title}</h2>
          {exam.subject && (
            <p class="text-sm text-muted-foreground">{exam.subject}</p>
          )}
        </div>

        {restoMs !== null && (
          <div class="mx-auto inline-flex items-center gap-2 rounded-md border border-primary/30 bg-background/60 px-4 py-2 text-sm font-bold tabular-nums">
            <Timer className="size-4 text-primary" />
            Começa em {formatHMS(restoMs)}
          </div>
        )}

        <div class="mx-auto grid max-w-md grid-cols-1 gap-3 text-left sm:grid-cols-2">
          {exam.startsAt && (
            <InfoLine
              icon={<Calendar className="size-4 text-primary" />}
              label="Início"
              value={new Date(exam.startsAt).toLocaleString("pt-BR")}
            />
          )}
          {exam.endsAt && (
            <InfoLine
              icon={<Flag className="size-4 text-primary" />}
              label="Fim"
              value={new Date(exam.endsAt).toLocaleString("pt-BR")}
            />
          )}
          {exam.durationMinutes != null && (
            <InfoLine
              icon={<Clock className="size-4 text-primary" />}
              label="Duração"
              value={`${exam.durationMinutes} min`}
            />
          )}
          <InfoLine
            icon={<Flag className="size-4 text-primary" />}
            label="Nota máxima"
            value={String(exam.maxScore)}
          />
        </div>

        {exam.instructions && (
          <article class="mx-auto max-w-2xl whitespace-pre-wrap rounded-md border border-border bg-background/40 px-4 py-3 text-left text-sm">
            <div class="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Instruções
            </div>
            {exam.instructions}
          </article>
        )}

        <p class="text-[11px] text-muted-foreground">
          O conteúdo das questões será liberado automaticamente no horário de
          início. Mantenha esta página aberta — ela atualiza sozinha.
        </p>
      </section>
    </div>
  );
}

function InfoLine({
  icon,
  label,
  value
}: {
  icon: any;
  label: string;
  value: string;
}) {
  return (
    <div class="flex items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-2">
      {icon}
      <div class="min-w-0 flex-1">
        <div class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div class="truncate text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}

// =====================
// Closed (depois do fim, sem release ainda)
// =====================

function Closed({ state }: { state: ScheduledExamState }) {
  return (
    <div class="space-y-4">
      <BackHeader />
      <section class="rounded-md border border-amber-500/30 bg-amber-500/10 px-6 py-8 text-center">
        <Hourglass className="mx-auto size-10 text-amber-600 dark:text-amber-400" />
        <h2 class="mt-3 text-base font-bold">Aguardando liberação do gabarito</h2>
        <p class="mt-2 text-sm text-muted-foreground">
          A prova encerrou. Você receberá o resultado quando o professor
          liberar o gabarito.
        </p>
        {state.exam?.endsAt && (
          <p class="mt-2 text-[11px] text-muted-foreground">
            Encerrou em{" "}
            {new Date(state.exam.endsAt).toLocaleString("pt-BR")}
          </p>
        )}
      </section>
    </div>
  );
}

// =====================
// Released (resultado liberado — modo consulta)
// Tem duas sub-views:
//   - "result" (default) → NOTA proeminente + breakdowns
//   - "revisao" → navegação questão-a-questão com gabarito + IA
// =====================

function Released({ state }: { state: ScheduledExamState }) {
  const escopo = state.questoes;

  // Conta acertos pro header — compartilhado entre as duas sub-views.
  const counts = useMemo(() => {
    let a = 0;
    let w = 0;
    let p = 0;
    let nul = 0;
    escopo.forEach((qq, i) => {
      const isAnul = state.anuladasIdx.includes(i);
      if (isAnul) {
        nul++;
        return;
      }
      const rec = state.answers[qq.id];
      if (!rec) {
        p++;
        return;
      }
      const corr = (Number(qq.correta) || 0) - 1;
      if (rec.opcao === corr) a++;
      else w++;
    });
    return { acertos: a, erros: w, pendentes: p, anuladas: nul };
  }, [escopo, state.anuladasIdx, state.answers]);

  if (releasedSubView.value === "result") {
    return <ResultSummary state={state} counts={counts} />;
  }
  return <ReleasedRevisao state={state} counts={counts} />;
}

interface ReleasedCounts {
  acertos: number;
  erros: number;
  pendentes: number;
  anuladas: number;
}

function ResultSummary({
  state,
  counts
}: {
  state: ScheduledExamState;
  counts: ReleasedCounts;
}) {
  const exam = state.exam!;
  // Score persistido pelo server (fonte de verdade pós-submit) tem prioridade
  // sobre o recompute local — evita "Nota 0" quando o cliente não tem todas
  // as respostas/anuladas/pesos exatamente como estavam na submissão.
  const score = state.attemptScore ?? computeScoreFromAnswers(state);
  const passingGrade = exam.passingGrade ?? 6;
  const passed = score >= passingGrade;
  const maxScoreNum = Number(exam.maxScore) || 10;
  const pctNota = Math.round((score / maxScoreNum) * 100);
  const total = state.questoes.length;
  const acertosNum = counts.acertos + counts.anuladas; // anuladas contam
  const pctAcerto =
    total > 0 ? Math.round((acertosNum / total) * 100) : 0;
  const tempoTotalMs = Object.values(state.tempoPorQuestao).reduce(
    (s, n) => s + n,
    0
  );
  const respondidasTotal = acertosNum + counts.erros;
  const tempoMedioMs =
    respondidasTotal > 0 ? Math.floor(tempoTotalMs / respondidasTotal) : 0;

  return (
    <div class="space-y-5">
      <BackHeader />
      {/* Hero da nota */}
      <section
        class={`overflow-hidden rounded-md border ${
          passed
            ? "border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-background"
            : "border-red-500/40 bg-gradient-to-br from-red-500/10 via-red-500/5 to-background"
        } px-6 py-6 sm:px-8 sm:py-8`}
      >
        <div class="flex flex-col items-center gap-2 text-center sm:flex-row sm:items-center sm:gap-6 sm:text-left">
          <div
            class={`flex flex-col items-center justify-center rounded-2xl px-6 py-4 shadow-lg ring-2 ${
              passed
                ? "bg-emerald-500 ring-emerald-500/40 text-white"
                : "bg-red-500 ring-red-500/40 text-white"
            }`}
          >
            <span class="text-[10px] font-bold uppercase tracking-wider opacity-90">
              Nota final
            </span>
            <span class="text-5xl font-black tabular-nums">
              {score.toFixed(2)}
            </span>
            <span class="text-[11px] font-semibold opacity-90">
              de {maxScoreNum.toFixed(0)} ({pctNota}%)
            </span>
          </div>
          <div class="min-w-0 flex-1 space-y-1">
            <div
              class={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-bold uppercase tracking-wider ${
                passed
                  ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                  : "bg-red-500/15 text-red-600 dark:text-red-400"
              }`}
            >
              {passed ? (
                <>
                  <Flag className="size-3" /> Aprovado
                </>
              ) : (
                <>
                  <XCircle className="size-3" /> Reprovado
                </>
              )}
            </div>
            <h1 class="text-lg font-bold sm:text-xl">{exam.title}</h1>
            {exam.subject && (
              <p class="text-xs text-muted-foreground">{exam.subject}</p>
            )}
            <p class="text-[11px] text-muted-foreground">
              Nota mínima de aprovação:{" "}
              <strong>{passingGrade.toFixed(2).replace(".", ",")}</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Stats em grid */}
      <section class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Acertos"
          value={`${counts.acertos}/${total}`}
          sub={`${pctAcerto}% de aproveitamento`}
          color="emerald"
        />
        <StatCard
          label="Erros"
          value={String(counts.erros)}
          sub={`${total > 0 ? Math.round((counts.erros / total) * 100) : 0}%`}
          color="red"
        />
        {counts.anuladas > 0 && (
          <StatCard
            label="Anuladas"
            value={String(counts.anuladas)}
            sub="contam como acerto"
            color="amber"
          />
        )}
        {counts.pendentes > 0 && (
          <StatCard
            label="Em branco"
            value={String(counts.pendentes)}
            sub={`${Math.round((counts.pendentes / total) * 100)}%`}
            color="muted"
          />
        )}
        <StatCard
          label="Tempo médio"
          value={formatHMS(tempoMedioMs)}
          sub={`Total ${formatHMS(tempoTotalMs)}`}
          color="muted"
        />
      </section>

      {/* CTA pra Revisão */}
      <section class="rounded-md border border-primary/30 bg-primary/5 px-5 py-4">
        <div class="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
          <div class="min-w-0 flex-1">
            <h2 class="text-sm font-bold text-primary">
              Revisar prova questão-a-questão
            </h2>
            <p class="mt-0.5 text-[11px] text-muted-foreground">
              Veja o gabarito, sua resposta em cada questão e use a IA para
              entender o que errou.
            </p>
          </div>
          <button
            type="button"
            onClick={() => (releasedSubView.value = "revisao")}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:bg-primary/90"
          >
            Revisar prova
            <ChevronRight className="size-4" />
          </button>
        </div>
      </section>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color
}: {
  label: string;
  value: string;
  sub: string;
  color: "emerald" | "red" | "amber" | "muted";
}) {
  const tone =
    color === "emerald"
      ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300"
      : color === "red"
        ? "border-red-500/30 bg-red-500/5 text-red-600 dark:text-red-400"
        : color === "amber"
          ? "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300"
          : "border-border bg-card/40 text-foreground";
  return (
    <div class={`rounded-md border px-4 py-3 ${tone}`}>
      <div class="text-[10px] font-bold uppercase tracking-wider opacity-80">
        {label}
      </div>
      <div class="mt-0.5 text-2xl font-black tabular-nums">{value}</div>
      <div class="text-[10px] opacity-70">{sub}</div>
    </div>
  );
}

function computeScoreFromAnswers(state: ScheduledExamState): number {
  if (!state.exam) return 0;
  const weights = state.exam.weights ?? [];
  let s = 0;
  state.questoes.forEach((q, i) => {
    const isAnul = state.anuladasIdx.includes(i);
    const w = weights[i] ?? 10 / Math.max(1, state.questoes.length);
    if (isAnul) {
      s += w;
      return;
    }
    const rec = state.answers[q.id];
    if (!rec) return;
    const corr = (Number(q.correta) || 0) - 1;
    if (rec.opcao === corr) s += w;
  });
  return Math.round(s * 100) / 100;
}

function ReleasedRevisao({
  state,
  counts
}: {
  state: ScheduledExamState;
  counts: ReleasedCounts;
}) {
  const exam = state.exam!;
  const total = state.questoes.length;
  const escopo = state.questoes;
  const idx = Math.max(0, Math.min(total - 1, state.currentIdx));
  const q = escopo[idx];

  const sortedQuestoes = useMemo(
    () => applySharedAgrupamento(escopo, examAgrupamento.value),
    [escopo, examAgrupamento.value]
  );
  const sortedIdx = useMemo(
    () => (q ? sortedQuestoes.findIndex((sq) => sq.id === q.id) : 0),
    [sortedQuestoes, q?.id]
  );
  // Número estável: escopo é a ordem fonte; preserva numero_original quando
  // existe. Independente de sort/filtro.
  const numOfQ = useMemo(() => {
    const m = new Map<string, number | string>();
    escopo.forEach((eq: any, i) => {
      const n = eq?.numero_original;
      m.set(String(eq.id), n != null && n !== "" ? n : i + 1);
    });
    return (qq: any) => m.get(String(qq.id)) ?? "?";
  }, [escopo]);

  const listSide = uiPrefs.value.questionListSide;

  if (!q) {
    return (
      <div class="space-y-4">
        <BackHeader />
        <p class="text-sm text-muted-foreground">Sem questões liberadas.</p>
      </div>
    );
  }

  return (
    <div class="space-y-4">
      <WorkspaceHeader
        mode="revisao"
        icon={Flag}
        iconTone="violet"
        title={exam.title}
        subtitle={
          <>
            {exam.subject ?? "Prova agendada"} · Questão {idx + 1} de {total}
          </>
        }
        summary={
          <div class="flex flex-wrap items-center gap-1.5 text-[11px]">
            <ResultChip label="Acertos" value={counts.acertos} color="emerald" />
            <ResultChip label="Erros" value={counts.erros} color="red" />
            {counts.anuladas > 0 && (
              <ResultChip label="Anuladas" value={counts.anuladas} color="amber" />
            )}
            <ResultChip label="Pendentes" value={counts.pendentes} color="muted" />
          </div>
        }
        actions={[
          {
            id: "voltar",
            icon: ArrowLeft,
            label: "Resultado",
            title: "Voltar para a tela de resultado",
            onClick: () => (releasedSubView.value = "result")
          },
          {
            id: "lista",
            icon: Flag,
            label: uiPrefs.value.questionListCollapsed ? "Mostrar lista" : "Esconder lista",
            title: "Lista (L)",
            onClick: () => toggleQuestionListCollapsed()
          }
        ]}
      />
      <div
        class={`flex flex-col gap-4 lg:items-start ${
          listSide === "right" ? "lg:flex-row-reverse" : "lg:flex-row"
        }`}
      >
        <div class="min-w-0 flex-1 space-y-4">
          <QuestionCard
            q={q}
            selected={state.answers[q.id]?.opcao ?? undefined}
            feedback="answered"
            showCorrectWhenWrong
            showExplanation
            tempoMs={state.tempoPorQuestao[q.id] ?? state.answers[q.id]?.tempoMs ?? 0}
          />
          <Nav
            idx={idx}
            total={total}
            onPrev={() => gotoIdx(idx - 1)}
            onNext={() => gotoIdx(idx + 1)}
          />
        </div>
        <QuestionListSidebar
          questoes={sortedQuestoes}
          currentIdx={Math.max(0, sortedIdx)}
          numOf={numOfQ}
          onPick={(si) => {
            const target = sortedQuestoes[si];
            if (!target) return;
            const u = escopo.findIndex((eq) => eq.id === target.id);
            if (u >= 0) gotoIdx(u);
          }}
          statusOf={(qq) => {
            const i = escopo.findIndex((e) => e.id === qq.id);
            if (i >= 0 && state.anuladasIdx.includes(i)) return "anulada";
            const rec = state.answers[qq.id];
            if (!rec) return "pending";
            const corr = (Number(qq.correta) || 0) - 1;
            return rec.opcao === corr ? "correct" : "wrong";
          }}
          tempoMsOf={(qq) =>
            state.tempoPorQuestao[qq.id] ??
            state.answers[qq.id]?.tempoMs ??
            0
          }
          agrupamento={examAgrupamento.value}
          onAgrupamentoChange={(a) => (examAgrupamento.value = a)}
          countLabel={`${total}`}
        />
      </div>
    </div>
  );
}

function ResultChip({
  label,
  value,
  color
}: {
  label: string;
  value: number;
  color: "emerald" | "red" | "muted" | "amber";
}) {
  const cls =
    color === "emerald"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
      : color === "red"
        ? "bg-red-500/15 text-red-600 dark:text-red-400"
        : color === "amber"
          ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
          : "bg-muted text-muted-foreground";
  return (
    <span class={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-semibold ${cls}`}>
      <span class="tabular-nums">{value}</span>
      <span class="opacity-80">{label}</span>
    </span>
  );
}

// =====================
// Running (em andamento)
// =====================

function Running({ state }: { state: ScheduledExamState }) {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  _examTick.value;
  const exam = state.exam!;
  const total = state.questoes.length;
  const idx = Math.max(0, Math.min(total - 1, state.currentIdx));
  const q = state.questoes[idx];

  // Atalhos
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (
        ev.target instanceof HTMLInputElement ||
        ev.target instanceof HTMLTextAreaElement
      )
        return;
      // Player aberto consome todos os atalhos (mesmo padrão do Study e Prova).
      // Sem isso, L do player também togglava a lista aqui. Karol 2026-05-24.
      if (isPlayerOpen.value) return;
      if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
      if (ev.key === "ArrowLeft") gotoIdx(idx - 1);
      else if (ev.key === "ArrowRight") gotoIdx(idx + 1);
      else if (ev.key === "l" || ev.key === "L") toggleQuestionListCollapsed();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx]);

  const sortedQuestoes = useMemo(
    () => applySharedAgrupamento(state.questoes, examAgrupamento.value),
    [state.questoes, examAgrupamento.value]
  );
  const sortedIdx = useMemo(
    () => (q ? sortedQuestoes.findIndex((sq) => sq.id === q.id) : 0),
    [sortedQuestoes, q?.id]
  );
  const numOfQ = useMemo(() => {
    const m = new Map<string, number | string>();
    state.questoes.forEach((eq: any, i) => {
      const n = eq?.numero_original;
      m.set(String(eq.id), n != null && n !== "" ? n : i + 1);
    });
    return (qq: any) => m.get(String(qq.id)) ?? "?";
  }, [state.questoes]);

  if (!q) {
    return (
      <div class="grid place-items-center py-16">
        <p class="text-sm text-muted-foreground">Sem questões disponíveis.</p>
      </div>
    );
  }

  const listSide = uiPrefs.value.questionListSide;

  // Tempo restante GLOBAL (server-authoritative — usa endsAt)
  const now = Date.now();
  const endsAtMs = exam.endsAt ? Date.parse(exam.endsAt) : null;
  const restoGlobalEndsAtMs = endsAtMs ? Math.max(0, endsAtMs - now) : null;

  // Cálculo do "Período aberto" (open_window): cronômetro INDIVIDUAL conta
  // a partir do attemptStartedAt do aluno + studentDurationMin. Quando
  // pausada (allowPause), descontamos o tempo pausado acumulado.
  let restoIndividualMs: number | null = null;
  const isOpenWindow = exam.examMode === "open_window";
  const isPaused = !!state.pauseMeta?.pausedAt;
  if (isOpenWindow && state.attemptStartedAt && exam.studentDurationMin) {
    const startMs = Date.parse(state.attemptStartedAt);
    const duracaoMs = exam.studentDurationMin * 60_000;
    const totalPausedMs = state.pauseMeta?.totalPausedMs ?? 0;
    // Se está pausado agora, congela o cronômetro adicionando o tempo
    // pausado em curso ao acumulado.
    const pausadoAgoraMs = isPaused
      ? now - Date.parse(state.pauseMeta!.pausedAt!)
      : 0;
    const elapsed = Math.max(0, now - startMs - totalPausedMs - pausadoAgoraMs);
    restoIndividualMs = Math.max(0, duracaoMs - elapsed);
  }
  // Em open_window, o cronômetro DOMINANTE é o individual; mas se a janela
  // global também está rolando, mostramos o menor dos dois (whichever
  // expires first wins).
  const restoGeralMs =
    isOpenWindow && restoIndividualMs != null
      ? restoGlobalEndsAtMs != null
        ? Math.min(restoIndividualMs, restoGlobalEndsAtMs)
        : restoIndividualMs
      : restoGlobalEndsAtMs;

  // Quantas respondidas (anuladas contam)
  const respondidas = useMemo(() => {
    let r = 0;
    state.questoes.forEach((qq, i) => {
      if (state.anuladasIdx.includes(i)) {
        r++;
        return;
      }
      if (state.answers[qq.id]) r++;
    });
    return r;
  }, [state.questoes, state.anuladasIdx, state.answers]);

  return (
    <div class="space-y-4">
      <WorkspaceHeader
        mode="prova"
        icon={Flag}
        iconTone="violet"
        title={exam.title}
        subtitle={
          <>
            {exam.subject ?? "Prova agendada"} · Questão {idx + 1} de {total} ·{" "}
            <span class="font-semibold">{respondidas}</span> respondida{respondidas === 1 ? "" : "s"}
          </>
        }
        summary={
          <ProvaAgendadaTimerRow
            restoGeralMs={restoGeralMs}
            tempoAtualMs={getQuestaoTempoMs(state, q.id)}
            pendentes={total - respondidas}
          />
        }
        actions={[
          ...(isOpenWindow && exam.allowPause
            ? [
                {
                  id: "pause",
                  icon: isPaused ? Play : Pause,
                  label: isPaused ? "Retomar" : "Pausar",
                  title: isPaused
                    ? "Retomar — cronômetro volta a correr"
                    : "Pausar — cronômetro congela. Use só pra interrupções breves; questões pendentes seguem acessíveis.",
                  tone: isPaused ? ("amber" as const) : ("neutral" as const),
                  onClick: () => void togglePause()
                }
              ]
            : []),
          {
            id: "lista",
            icon: Flag,
            label: uiPrefs.value.questionListCollapsed ? "Mostrar lista" : "Esconder lista",
            title: "Lista (L)",
            onClick: () => toggleQuestionListCollapsed()
          },
          {
            id: "encerrar",
            icon: Send,
            label: state.submitting ? "Encerrando…" : "Encerrar prova",
            title: "Enviar respostas e encerrar",
            tone: "primary",
            onClick: () => (submitConfirmOpen.value = true)
          }
        ]}
      />
      {isPaused && (
        <div class="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          <div class="flex items-center gap-2 font-bold">
            <Pause className="size-4" /> Prova pausada
          </div>
          <p class="mt-1 text-xs text-amber-700/90 dark:text-amber-300/90">
            Seu cronômetro está congelado e suas respostas ficam intactas. Clique em{" "}
            <strong>Retomar</strong> no header quando estiver pronto pra continuar.
          </p>
        </div>
      )}
      <div
        class={`flex flex-col gap-4 lg:items-start ${
          listSide === "right" ? "lg:flex-row-reverse" : "lg:flex-row"
        }`}
      >
        <div class="min-w-0 flex-1 space-y-4">
          <QuestionCard
            q={q}
            selected={state.answers[q.id]?.opcao}
            onSelect={(i) => setAnswer(q.id, i)}
            onClear={() => setAnswer(q.id, null)}
            feedback="none"
            tempoMs={getQuestaoTempoMs(state, q.id)}
          />
          <Nav
            idx={idx}
            total={total}
            onPrev={() => gotoIdx(idx - 1)}
            onNext={() => gotoIdx(idx + 1)}
          />
        </div>
        <QuestionListSidebar
          questoes={sortedQuestoes}
          currentIdx={Math.max(0, sortedIdx)}
          numOf={numOfQ}
          onPick={(si) => {
            const target = sortedQuestoes[si];
            if (!target) return;
            const u = state.questoes.findIndex((eq) => eq.id === target.id);
            if (u >= 0) gotoIdx(u);
          }}
          statusOf={(qq): QListStatus => {
            const i = state.questoes.findIndex((e) => e.id === qq.id);
            if (i >= 0 && state.anuladasIdx.includes(i)) return "anulada";
            return state.answers[qq.id] ? "answered" : "pending";
          }}
          tempoMsOf={(qq) => getQuestaoTempoMs(state, qq.id)}
          agrupamento={examAgrupamento.value}
          onAgrupamentoChange={(a) => (examAgrupamento.value = a)}
          countLabel={`${total}`}
        />
      </div>
    </div>
  );
}

function Nav({
  idx,
  total,
  onPrev,
  onNext
}: {
  idx: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <nav class="flex items-center justify-between gap-3 rounded-md border border-border bg-card/40 px-4 py-3">
      <button
        type="button"
        onClick={onPrev}
        disabled={idx === 0}
        title="Anterior (←)"
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ChevronLeft width="1.2em" height="1.2em" /> Anterior
      </button>
      <span class="text-xs tabular-nums text-muted-foreground">
        {idx + 1} / {total}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={idx >= total - 1}
        title="Próxima (→)"
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        Próxima <ChevronRight width="1.2em" height="1.2em" />
      </button>
    </nav>
  );
}

// =====================
// Header simples (lobby / closed)
// =====================

function BackHeader() {
  return (
    <div class="flex items-center justify-between gap-3">
      <button
        type="button"
        onClick={() => exitScheduledExamView()}
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
      >
        <ArrowLeft className="size-3.5" />
        Minhas provas
      </button>
    </div>
  );
}

// =====================
// Modal de confirmação de encerramento
// =====================

function SubmitConfirmModal({ state }: { state: ScheduledExamState }) {
  const total = state.questoes.length;
  const respondidas = useMemo(() => {
    let r = 0;
    state.questoes.forEach((qq, i) => {
      if (state.anuladasIdx.includes(i)) {
        r++;
        return;
      }
      if (state.answers[qq.id]) r++;
    });
    return r;
  }, [state.questoes, state.anuladasIdx, state.answers]);
  const pendentes = total - respondidas;
  return (
    <div
      class="fixed inset-0 z-[90] grid place-items-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) submitConfirmOpen.value = false;
      }}
    >
      <div class="w-full max-w-md space-y-4 rounded-md border border-border bg-card p-5 shadow-2xl">
        <div class="flex items-center gap-2">
          <Send className="size-5 text-primary" />
          <h3 class="text-base font-bold">Encerrar prova?</h3>
        </div>
        {pendentes > 0 ? (
          <p class="text-sm text-muted-foreground">
            Você ainda tem{" "}
            <strong class="text-amber-600 dark:text-amber-400">
              {pendentes} questão{pendentes === 1 ? "" : "ões"} sem responder
            </strong>
            . Após encerrar, não dá pra voltar.
          </p>
        ) : (
          <p class="text-sm text-muted-foreground">
            Todas as questões respondidas. Confirme pra enviar.
          </p>
        )}
        <div class="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => (submitConfirmOpen.value = false)}
            disabled={state.submitting}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
          >
            Continuar respondendo
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={state.submitting}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {state.submitting ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Send className="size-3.5" />
            )}
            Encerrar agora
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================
// Timer row da Running — 3 pílulas: restante geral, atual e ritmo
// =====================

function ProvaAgendadaTimerRow({
  restoGeralMs,
  tempoAtualMs,
  pendentes
}: {
  restoGeralMs: number | null;
  tempoAtualMs: number;
  pendentes: number;
}) {
  // Cor da pílula "restante" muda conforme aperta o tempo:
  //   - > 30min: primary
  //   - 5–30min: amber
  //   - < 5min: red, com pulse
  let restoTone = "border-primary/40 bg-primary/10 text-primary";
  let restoPulse = "";
  if (restoGeralMs != null) {
    if (restoGeralMs < 5 * 60_000) {
      restoTone =
        "border-red-500/60 bg-red-500/15 text-red-700 dark:text-red-300";
      restoPulse = "animate-pulse";
    } else if (restoGeralMs < 30 * 60_000) {
      restoTone =
        "border-amber-500/50 bg-amber-500/15 text-amber-700 dark:text-amber-300";
    }
  }
  // Ritmo: tempo restante ÷ pendentes
  const ritmoMs =
    restoGeralMs != null && pendentes > 0 ? Math.floor(restoGeralMs / pendentes) : null;
  return (
    <div class="flex flex-wrap items-center gap-1.5">
      {restoGeralMs != null && (
        <span
          class={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-bold tabular-nums ${restoTone} ${restoPulse}`}
          title="Tempo restante na janela da prova"
        >
          <Clock className="size-3" />
          {formatHMS(restoGeralMs)}
        </span>
      )}
      <span
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background/80 px-2.5 py-1 text-[11px] font-bold tabular-nums text-foreground"
        title="Tempo nesta questão"
      >
        <Timer className="size-3" />
        {formatHMS(tempoAtualMs)}
      </span>
      {ritmoMs != null && (
        <span
          class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background/60 px-2.5 py-1 text-[11px] font-semibold tabular-nums text-muted-foreground"
          title="Tempo médio sugerido por questão pendente"
        >
          <Hourglass className="size-3" />
          {formatHMS(ritmoMs)}/q
        </span>
      )}
    </div>
  );
}
